import { Badge } from '@/components/ui/badge';
import React, { FC, useMemo, useRef, useEffect } from 'react';
import { Cart, Order, trpc } from 'src/trpc';
import { Loader } from 'components/Loader';
import { MoneyErrorBoundary } from 'components/MoneyErrorBoundary';
import {
  itemizedOrderWithAdjustments,
  safeMoneyFromCents,
  MoneyFromDollars,
  toItemizedOrder,
  toItemizedOrderInCents,
} from '@libs/services/money';
import { BundleChargeSummary } from 'components/ChargeSummary/BundleChargeSummary';
import { FixedChargeSummary } from 'components/ChargeSummary/FixedChargeSummary';
import { PayAsYouGoChargeSummary } from 'components/ChargeSummary/PayAsYouGoChargeSummary';
import { AdditionalPaymentsSection } from 'components/ChargeSummary/AdditionalPaymentsSection';
import { PricingTypeEnum, ContainerBreakdown } from '@libs/schemas';

interface Props {
  order: Order;
  cart: Cart;
  weightCostAdjInCents?: number;
  materialCostAdjInCents?: number;
  containerCostCents?: number;
  discountInCents?: number;
  finalWeight?: number;
  additionalMaterials?: { [materialId: string]: number };
  containers?: { _id?: string; containerTypeInfo: { size: string; name: string } }[];
  usageByContainer?: { [containerId: string]: number };
  containerBreakdown?: ContainerBreakdown[];
  setTotalBalanceInCents?: (total: number) => void;
  children?: ({ balanceDue }: { balanceDue: number }) => React.ReactNode | undefined;
}

export const ChargeSummary: FC<Props> = ({
  order,
  cart,
  weightCostAdjInCents,
  materialCostAdjInCents,
  containerCostCents,
  children,
  finalWeight,
  additionalMaterials,
  containerBreakdown,
  setTotalBalanceInCents,
}) => {
  const { data: materials } = trpc.admin.material.getMaterials.useQuery(undefined, {
    staleTime: Infinity,
    gcTime: Infinity,
    suspense: false,
    refetchOnWindowFocus: false,
  });

  // Trust the values from extractChargeSummaryPropsFromFinalizedOrder utility
  // All calculations are done in the utility - ChargeSummary is just a display component
  const adjustedCosts = useMemo(() => {
    return {
      weightCostAdjustedInCents: weightCostAdjInCents || 0,
      materialCostAdjustedInCents: materialCostAdjInCents || 0,
      containerCostAdjustedInCents: containerCostCents || 0,
      totalAdjustedInCents:
        (weightCostAdjInCents || 0) + (materialCostAdjInCents || 0) + (containerCostCents || 0),
    };
  }, [weightCostAdjInCents, materialCostAdjInCents, containerCostCents]);

  const itemizedOrderInCents = useMemo(() => {
    return toItemizedOrderInCents(
      itemizedOrderWithAdjustments({
        order: { ...order, cart },
        adjustedWeightTotal: safeMoneyFromCents(adjustedCosts.weightCostAdjustedInCents),
        adjustedContainerTotal: safeMoneyFromCents(adjustedCosts.containerCostAdjustedInCents),
        adjustedMaterialTotal: safeMoneyFromCents(adjustedCosts.materialCostAdjustedInCents),
      }),
    );
  }, [order, cart, adjustedCosts]);

  const itemizedOrder = useMemo(
    () => toItemizedOrder(itemizedOrderInCents),
    [itemizedOrderInCents],
  );

  const { data: newTaxInCents, isLoading: taxLoading } =
    trpc.admin.order.adminCalculateTaxes.useQuery(
      {
        subTotal: itemizedOrder.totalBeforeTax.getAmount(),
        userId: order.userId,
        orderId: order._id!,
      },
      {
        staleTime: 30000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
      },
    );

  // The adminCalculateTaxes endpoint now handles all tax logic internally,
  // respecting the order's original collectTaxes flag and status
  const effectiveTaxInCents = useMemo(() => {
    return newTaxInCents || 0;
  }, [newTaxInCents]);

  const { displayNewTotal, balanceDue } = useMemo(() => {
    const tax = safeMoneyFromCents(effectiveTaxInCents);

    // Display total: actual cost after adjustments (without refunds)
    const displayNewTotal = itemizedOrder.totalBeforeTax
      .add(tax)
      .add(itemizedOrder.postTaxAdditionalLineItemsTotal);

    // Accounting total: includes refunds to balance the books
    const accountingTotal = displayNewTotal.add(
      safeMoneyFromCents(order.refunds?.reduce((acc, p) => acc + p.amountRefunded, 0) || 0),
    );

    const totalPayments = safeMoneyFromCents(
      order.payments?.reduce((acc, p) => acc + p.amountPaid, 0) || 0,
    );
    const balanceDue = accountingTotal.subtract(totalPayments);
    return { displayNewTotal, balanceDue };
  }, [itemizedOrder, effectiveTaxInCents, order.refunds, order.payments]);

  // Use useEffect with proper dependencies
  const currentBalanceDue = balanceDue.getAmount();

  // Use useEffect but with a ref to prevent infinite loops
  const setTotalBalanceInCentsRef = useRef(setTotalBalanceInCents);
  setTotalBalanceInCentsRef.current = setTotalBalanceInCents;

  useEffect(() => {
    if (setTotalBalanceInCentsRef.current) {
      setTotalBalanceInCentsRef.current(currentBalanceDue);
    }
  }, [currentBalanceDue]); // Only depend on the value, not the function

  const additionalPayments = useMemo(() => {
    return order.payments?.slice(1) || [];
  }, [order.payments]);

  return (
    <MoneyErrorBoundary>
      <div className="flex flex-col gap-2 w-full font-bold pt-4">
        <div className="card bg-secondary flex flex-col gap-2 w-full font-bold p-0">
          <div className="p-4 flex flex-col gap-2 w-full">
            <div className="flex flex-row justify-between w-full">
              <h6 className="text-text-primary text-sm">New Total</h6>
              <span className="text-text-primary text-sm">
                {!taxLoading && <span>{displayNewTotal.toFormat()}</span>}
                {taxLoading && (
                  <span>
                    <Loader h={5} w={5} />
                  </span>
                )}
              </span>
            </div>
            <div className="h-[1px] w-full bg-disabled"></div>
            <div className="flex flex-row justify-between w-full">
              <div className="flex flex-row  items-center gap-1">
                <h6 className="text-text-primary text-sm">Original Balance</h6>{' '}
                <Badge variant="success">Paid</Badge>
              </div>
              <span className="text-text-primary text-sm">
                - {itemizedOrder.originalTotal.toFormat()}
              </span>
            </div>
            <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
              <h6>Subtotal</h6>
              <span>{itemizedOrder.originalSubtotal.toFormat()}</span>
            </div>
            <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
              <h6>Tax</h6>
              <span>{itemizedOrder.originalTax.toFormat()}</span>
            </div>

            <div className="h-[1px] w-full bg-disabled"></div>
            <div className="flex flex-row justify-between w-full">
              <h6 className="text-text-primary text-sm">Adjustments</h6>
              <span className="text-text-primary text-sm">
                {safeMoneyFromCents(adjustedCosts.totalAdjustedInCents).toFormat()}
              </span>
            </div>
            {/* Render pricing-specific adjustments based on pricing model */}
            {(() => {
              const pricingModel =
                order.quotedPricingDetails?.pricingModel || PricingTypeEnum.PAY_AS_YOU_GO;

              switch (pricingModel) {
                case PricingTypeEnum.BUNDLE:
                  return (
                    <BundleChargeSummary
                      order={order}
                      finalWeight={finalWeight}
                      additionalMaterials={additionalMaterials}
                      allAvailableMaterials={materials}
                      containerBreakdown={containerBreakdown}
                    />
                  );
                case PricingTypeEnum.FIXED:
                  return (
                    <FixedChargeSummary
                      order={order}
                      finalWeight={finalWeight}
                      additionalMaterials={additionalMaterials}
                      allAvailableMaterials={materials}
                      containerBreakdown={containerBreakdown}
                    />
                  );
                case PricingTypeEnum.PAY_AS_YOU_GO:
                default:
                  return (
                    <PayAsYouGoChargeSummary
                      order={order}
                      cart={cart}
                      finalWeight={finalWeight}
                      additionalMaterials={additionalMaterials}
                      allAvailableMaterials={materials}
                      containerBreakdown={containerBreakdown}
                      containerCostCents={containerCostCents}
                    />
                  );
              }
            })()}
            {order?.addOns && order.addOns.length > 0 ? (
              <>
                <div className="h-[1px] w-full bg-disabled"></div>
                <div className="flex flex-row justify-between w-full text-sm">
                  <h6 className="text-text-primary">Add-ons</h6>
                  <span>
                    {safeMoneyFromCents(
                      order.addOns.reduce((acc, addOn) => acc + addOn.priceInCents, 0),
                    ).toFormat()}
                  </span>
                </div>
                {order.addOns.map((addOn) => (
                  <div
                    key={addOn._id}
                    className="flex flex-row justify-between w-full font-light text-xs text-muted"
                  >
                    <h6>{addOn.title}</h6>
                    <span>{safeMoneyFromCents(addOn.priceInCents).toFormat()}</span>
                  </div>
                ))}
              </>
            ) : null}

            {order?.additionalLineItems?.filter((i) => !i.postTax).length ? (
              <>
                <div className="h-[1px] w-full bg-disabled"></div>
                <div className="flex flex-row justify-between w-full text-sm">
                  <h6 className="text-text-primary">Additional Charges (taxable)</h6>
                  <span>{itemizedOrder.preTaxAdditionalLineItemsTotal.toFormat()}</span>
                </div>
                {order.additionalLineItems
                  .filter((i) => !i.postTax)
                  .map((i) => (
                    <div key={i.label} className="flex flex-row justify-between w-full text-xs">
                      <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
                        <h6>{i.label}</h6>
                        <span>{MoneyFromDollars(i.amount).toFormat()}</span>
                      </div>
                    </div>
                  ))}
              </>
            ) : null}

            {order?.additionalLineItems?.filter((i) => i.postTax).length ? (
              <>
                <div className="h-[1px] w-full bg-disabled"></div>
                <div className="flex flex-row justify-between w-full text-sm">
                  <h6 className="text-text-primary text-sm">Additional Charges (non-taxable)</h6>
                  <span>{itemizedOrder.postTaxAdditionalLineItemsTotal.toFormat()}</span>
                </div>
                {order.additionalLineItems
                  .filter((i) => i.postTax)
                  .map((i) => (
                    <div className="flex flex-row justify-between w-full text-xs">
                      <div
                        key={i.label}
                        className="flex flex-row justify-between w-full font-light text-xs text-muted"
                      >
                        <h6>{i.label}</h6>
                        <span>{MoneyFromDollars(i.amount).toFormat()}</span>
                      </div>
                    </div>
                  ))}
              </>
            ) : null}

            <div className="flex flex-row justify-between w-full text-xs text-muted font-light">
              <h6>Tax</h6>
              {!taxLoading && (
                <span>
                  {(() => {
                    // If the order doesn't collect taxes, show $0.00
                    if (order.collectTaxes === false) {
                      return safeMoneyFromCents(0).toFormat();
                    }

                    // For orders that collect taxes, show the tax difference (effective tax - original tax)
                    // This will show the actual adjustment taxes that were collected or refunded
                    const taxDifference = safeMoneyFromCents(effectiveTaxInCents).subtract(
                      itemizedOrder.originalTax || 0,
                    );

                    return taxDifference.toFormat();
                  })()}
                </span>
              )}
              {taxLoading && (
                <span>
                  <Loader h={5} w={5} />
                </span>
              )}
            </div>

            <div className="flex flex-row justify-between w-full text-xs text-success">
              <p>DISCOUNT</p>
              <span>- {itemizedOrder.discount.toFormat()}</span>
            </div>
            {/* Display all payments */}
            {additionalPayments && additionalPayments.length > 0 && (
              <>
                <div className="h-[1px] w-full bg-disabled"></div>
                <AdditionalPaymentsSection additionalPayments={additionalPayments} />
              </>
            )}
          </div>
          <div className="flex flex-row bg-slate-800 rounded-b-lg justify-between w-full text-white p-4 text-base">
            <h6>Balance Due</h6>
            {!taxLoading && <span>{balanceDue.toFormat()}</span>}
            {taxLoading && (
              <span>
                <Loader h={5} w={5} />
              </span>
            )}
          </div>
        </div>
        {children
          ? children({
              balanceDue: balanceDue.getAmount(),
            })
          : null}
      </div>
    </MoneyErrorBoundary>
  );
};
